









import Vue, { PropType } from 'vue';
import { Bar, mixins } from 'vue-chartjs';
import { UserMetricsGranularity, UserMetricsTimelinePoint } from '@/types';

const BarChart = Vue.extend({
  extends: Bar,
  mixins: [mixins.reactiveProp],
  props: {
    options: {
      type: Object,
      required: true,
    },
    styles: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    // reactiveProp updates an existing chart, but does not perform its first draw.
    // Render explicitly once the visible Trend tab has supplied a real canvas size.
    (this as any).renderChart((this as any).chartData, (this as any).options);
  },
});

export default Vue.extend({
  name: 'UserMetricsTrendChart',
  components: { BarChart },
  props: {
    granularity: {
      type: String as PropType<UserMetricsGranularity>,
      required: true,
    },
    series: {
      type: Array as PropType<UserMetricsTimelinePoint[]>,
      required: true,
    },
  },
  computed: {
    chartData(): Chart.ChartData {
      const labels = this.series.map((point) => this.formatPeriod(point.period));
      const dataset = (label: string, key: keyof UserMetricsTimelinePoint, color: string, axis: string) => ({
        label,
        data: this.series.map((point) => Number(point[key]) || 0),
        backgroundColor: color,
        borderColor: color,
        borderWidth: 0,
        yAxisID: axis,
      });
      return {
        labels,
        datasets: [
          dataset('Jobs', 'jobs', '#4F7CFF', 'jobs-axis'),
          dataset('Frames', 'frames', '#17A98C', 'frames-axis'),
          dataset('Videos', 'videos', '#F59E0B', 'videos-axis'),
          dataset('Storage', 'storage_gb', '#9B6BDE', 'storage-axis'),
        ],
      };
    },
    chartOptions(): Chart.ChartOptions {
      const darkTheme = Boolean((this as any).$vuetify && (this as any).$vuetify.theme.dark);
      const textColor = darkTheme ? 'rgba(255, 255, 255, 0.78)' : 'rgba(0, 0, 0, 0.68)';
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'top',
          labels: {
            boxWidth: 10,
            fontColor: textColor,
            fontSize: 12,
            padding: 14,
          },
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label(tooltipItem: Chart.ChartTooltipItem, data: Chart.ChartData) {
              const label = data.datasets![tooltipItem.datasetIndex!].label || '';
              const value = Number(tooltipItem.yLabel) || 0;
              const formatted = label === 'Storage'
                ? `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`
                : new Intl.NumberFormat().format(value);
              return `${label}: ${formatted}`;
            },
          },
        },
        scales: {
          xAxes: [{
            gridLines: { display: false },
            ticks: {
              autoSkip: false,
              display: false,
            },
          }],
          // Independent hidden axes let each unit remain visible in one grouped chart.
          yAxes: ['jobs-axis', 'frames-axis', 'videos-axis', 'storage-axis'].map((id) => ({
            id,
            display: false,
            ticks: { beginAtZero: true },
            gridLines: { display: false },
          })),
        },
      };
    },
    chartStyles(): Record<string, string> {
      return { height: '250px', position: 'relative' };
    },
  },
  methods: {
    formatPeriod(period: string): string {
      if (this.granularity === 'year') return period;
      const values = period.split('-').map(Number);
      const date = this.granularity === 'month'
        ? new Date(values[0], values[1] - 1, 1)
        : new Date(values[0], values[1] - 1, values[2]);
      return this.granularity === 'month'
        ? date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
        : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    },
  },
});
